import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical } from 'lucide-react';

const fieldTypes = [
  { value: 'text', label: 'Short Text' },
  { value: 'longtext', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File / Image' },
  { value: 'url', label: 'URL' },
];

const entityTypes = [
  { value: 'lead', label: 'Leads' },
  { value: 'project', label: 'Projects' },
  { value: 'task', label: 'Tasks' },
  { value: 'team', label: 'Team Members' },
];

export default function CustomFieldsEditor() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEntity, setActiveEntity] = useState('lead');
  const [newField, setNewField] = useState({ label: '', field_type: 'text', options: '', entity_type: 'lead' });

  const loadFields = async () => {
    try {
      const promises = entityTypes.map(e => api.get(`/custom-fields/${e.value}`));
      const results = await Promise.all(promises);
      const allFields = results.flatMap(r => r.data);
      setFields(allFields);
    } catch { toast.error('Failed to load custom fields'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadFields(); }, []);

  const addField = async () => {
    if (!newField.label.trim()) { toast.error('Label required'); return; }
    try {
      const payload = {
        ...newField,
        entity_type: activeEntity,
        options: newField.field_type === 'dropdown' ? newField.options.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await api.post('/custom-fields', payload);
      toast.success('Custom field added');
      setNewField({ label: '', field_type: 'text', options: '', entity_type: activeEntity });
      loadFields();
    } catch { toast.error('Failed to add field'); }
  };

  const deleteField = async (id) => {
    try {
      await api.delete(`/custom-fields/${id}`);
      toast.success('Field deleted');
      loadFields();
    } catch { toast.error('Delete failed'); }
  };

  const entityFields = fields.filter(f => f.entity_type === activeEntity);

  if (loading) return <div className="flex items-center justify-center h-20"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="custom-fields-editor">
      {/* Entity tabs */}
      <div className="flex gap-1 bg-[#E8E6E1]/50 rounded-xl p-1 w-fit">
        {entityTypes.map(e => (
          <button key={e.value} onClick={() => setActiveEntity(e.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeEntity === e.value ? 'bg-white text-[#2C2C2E] shadow-sm' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}
            data-testid={`cf-tab-${e.value}`}>
            {e.label}
          </button>
        ))}
      </div>

      {/* Existing fields */}
      <div className="space-y-2">
        {entityFields.map(f => (
          <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1] group" data-testid={`custom-field-${f.id}`}>
            <GripVertical size={14} strokeWidth={1.5} className="text-[#D4D0C8]" />
            <span className="text-sm font-medium text-[#2C2C2E] flex-1">{f.label}</span>
            <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[9px]">{fieldTypes.find(ft => ft.value === f.field_type)?.label || f.field_type}</Badge>
            {f.options?.length > 0 && <span className="text-[10px] text-[#6B7280]">{f.options.join(', ')}</span>}
            <button onClick={() => deleteField(f.id)} className="opacity-0 group-hover:opacity-100 p-1 text-[#C4727F] hover:bg-[#C4727F]/10 rounded-lg transition-all" data-testid={`cf-delete-${f.id}`}>
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        ))}
        {entityFields.length === 0 && <p className="text-xs text-[#6B7280] text-center py-4">No custom fields for {entityTypes.find(e => e.value === activeEntity)?.label}</p>}
      </div>

      {/* Add new field */}
      <div className="flex items-end gap-2 p-3 rounded-xl border border-dashed border-[#D4D0C8]">
        <div className="flex-1">
          <Label className="text-[10px] text-[#6B7280] uppercase">Label</Label>
          <Input value={newField.label} onChange={e => setNewField({...newField, label: e.target.value})}
            placeholder="Field name" className="mt-1 h-8 rounded-lg border-[#D4D0C8] text-sm" data-testid="cf-new-label" />
        </div>
        <div className="w-32">
          <Label className="text-[10px] text-[#6B7280] uppercase">Type</Label>
          <Select value={newField.field_type} onValueChange={v => setNewField({...newField, field_type: v})}>
            <SelectTrigger className="mt-1 h-8 rounded-lg border-[#D4D0C8] text-xs" data-testid="cf-new-type"><SelectValue /></SelectTrigger>
            <SelectContent>{fieldTypes.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {newField.field_type === 'dropdown' && (
          <div className="flex-1">
            <Label className="text-[10px] text-[#6B7280] uppercase">Options (comma separated)</Label>
            <Input value={newField.options} onChange={e => setNewField({...newField, options: e.target.value})}
              placeholder="Option1, Option2" className="mt-1 h-8 rounded-lg border-[#D4D0C8] text-sm" />
          </div>
        )}
        <button onClick={addField} className="h-8 px-3 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-1" data-testid="cf-add-btn">
          <Plus size={13} /> Add
        </button>
      </div>
    </div>
  );
}
